import * as React from "react";
import { DateOptions, Contents } from "./Resource/Contents";
import { DatePicker } from "../../../react-core/Datepicker/DatePicker";
import { SelectEditorFactory } from "../../../react-core/Html/Factories/SelectEditorFactory";
import { CommonHelper } from "../../../react-core/Helpers/CommonHelper";
import { BaseComponent } from "../../../react-core/Html/BaseComponent";
import { JsonDateParser } from "../../../react-core/Utils/JsonDateParser";
import { addMonths } from "../../../react-core/utils/date";
import { ClientAccountStore } from "../../Dashboard/Stroes/Variable";
import {
    PortfolioDashboardStore,
    ConsolidatedAccountStore,
} from "../../Dashboard/Components/Clients/PortfolioDashboard/Store/Variable";
import classNames from "classnames";
import { HierarchyIdHelper } from "../Common/HierarchyIdHelper";

export interface IDateRangeProps extends React.Props<DateRange> {
    minDate: Date;
    selectValue: string;
    fromDate?: Date;
    toDate?: Date;
    disabled?: boolean;
    maxDate?: Date;
    label?: string;
    excludeSinceAccountOpening?: boolean;
    onChange: (
        fromDate: Date,
        toDate: Date,
        fromInception: boolean,
        dateSelected: string,
        isFromAndTo?: boolean
    ) => void;
    excludeToday?: boolean;
    cssGridDate?: string;
    cssGridTo?: string;
    cssGridFrom?: string;
    cssGridLabel?: string;
    useShortMonthName?: boolean;
}

export interface IDateRangeState {
    fromMinDate?: Date;
    dateSelected?: string;
    fromeDateSelected?: Date;
    toDateSelected?: Date;
    useShortMonthName?: boolean;
}

export class DateRange extends BaseComponent<IDateRangeProps, IDateRangeState> {
    constructor(props: IDateRangeProps) {
        super(props);
        const now = props.maxDate || new Date();

        const fromDate = this.getFromDate();
        const _minDate = this.getMinDate(props.minDate);
        this.state = {
            fromMinDate: _minDate,
            dateSelected: props.selectValue ? props.selectValue : DateOptions[5].key,
            fromeDateSelected: fromDate,
            toDateSelected: props.toDate ? props.toDate : now,
            useShortMonthName: props.useShortMonthName || false,
        };
    }
    public static getLeastDate = (accountViewSelected, isConsolidated?) => {
        const clientInfo = ClientAccountStore.getClientAccountInfo();
        let inceptionDate = new Date();
        let _dateDisable = false;
        let wpAccount = "";
        if (clientInfo) {
            if (
                !CommonHelper.isNullOrEmpty(accountViewSelected) &&
                HierarchyIdHelper.Equals(accountViewSelected, clientInfo.HierarchyId)
            ) {
                inceptionDate = new Date(clientInfo.InceptionDate as any);
                wpAccount = clientInfo.HeadClAccountId;
                _dateDisable = JsonDateParser.getFormatDate(clientInfo.InceptionDate, "yyyy") == "1" ? true : false;
                if (_dateDisable == true) {
                    const aYearFromNow = new Date();
                    aYearFromNow.setFullYear(aYearFromNow.getFullYear() - 1);
                    inceptionDate = aYearFromNow;
                }
            } else {
                const findObj = clientInfo.SubAccounts.filter((x) => x.HierarchyId == accountViewSelected)[0];
                if (findObj) {
                    inceptionDate = new Date(findObj.InceptionDate as any);
                    wpAccount = findObj.ClAccountId;
                    _dateDisable = JsonDateParser.getFormatDate(findObj.InceptionDate, "yyyy") == "1" ? true : false;
                }
            }
        } else {
            let clients = PortfolioDashboardStore.getData();
            if (isConsolidated) {
                clients = [ConsolidatedAccountStore.getData()].concat(clients);
            }
            if (clients.length > 0) {
                clients.map((item) => {
                    if (
                        !CommonHelper.isNullOrEmpty(accountViewSelected) &&
                        HierarchyIdHelper.Equals(accountViewSelected, item.HierarchyId)
                    ) {
                        inceptionDate = new Date(item.InceptionDate as any);
                        wpAccount = item.HeadClAccountId;
                        _dateDisable = JsonDateParser.getFormatDate(item.InceptionDate, "yyyy") == "1" ? true : false;
                    } else {
                        const findObj = item.SubAccounts.filter((x) => x.HierarchyId == accountViewSelected)[0];
                        if (findObj) {
                            inceptionDate = new Date(findObj.InceptionDate as any);
                            wpAccount = findObj.ClAccountId;
                            _dateDisable =
                                JsonDateParser.getFormatDate(findObj.InceptionDate, "yyyy") == "1" ? true : false;
                        }
                    }
                });
            } else {
                inceptionDate = new Date(
                    inceptionDate.getFullYear() - 1,
                    inceptionDate.getMonth(),
                    inceptionDate.getDate() + 1
                );
            }
        }
        return { minDate: inceptionDate, isDisable: _dateDisable, wpAccount: wpAccount };
    };

    public static initializeDateRange = (openingDate?: Date): any => {
        const now = new Date();
        const daterange = {
            from: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate() + 1),
            to: now,
            dateSelected: DateOptions[5].key,
            fromInception: false,
        };
        if (openingDate == null) {
            return daterange;
        }
        if (openingDate > daterange.from) {
            daterange.dateSelected = DateOptions[0].key;
            daterange.from = openingDate;
            daterange.fromInception = true;
        }
        return daterange;
    };

    public static initializeLastYearDateRange = (openingDate?: Date): any => {
        const now = new Date();
        const daterange = {
            from: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() + 1),
            to: now,
            dateSelected: DateOptions[3].key,
            fromInception: false,
        };
        if (openingDate == null) {
            return daterange;
        }
        if (openingDate > daterange.from) {
            daterange.dateSelected = DateOptions[0].key;
            daterange.from = openingDate;
            daterange.fromInception = true;
        }
        return daterange;
    };

    private getMinDate(date: Date) {
        const _minDate = date ? new Date(JsonDateParser.getFormatDateString(date, "yyyy/MM/dd")) : null;
        return _minDate;
    }

    private isLeapDay(toDate: Date) {
        const year = toDate.getFullYear();
        const isLeapYear = year % 100 === 0 ? year % 400 === 0 : year % 4 === 0;
        const leapdaystr = toDate.getFullYear() + "/02/29";
        const todaystr = JsonDateParser.getFormatDateString(toDate, "yyyy/MM/dd");
        return isLeapYear && todaystr === leapdaystr;
    }

    private getFromDate(): Date {
        const { selectValue, minDate, fromDate, toDate, onChange } = this.props;
        let _date = fromDate;
        if (selectValue == DateOptions[3].key) {
            _date = this.getPastYear(toDate);
        }
        if (selectValue == null || selectValue == DateOptions[0].key) {
            _date = minDate;
        }
        if (!_date) {
            _date = this.getPastYear(new Date());
        }
        if (fromDate && _date.getTime() !== fromDate.getTime()) {
            onChange(_date, toDate, selectValue == DateOptions[0].key, selectValue);
        }
        return _date;
    }

    private getPastYear(toDate: Date): Date {
        return this.isLeapDay(toDate)
            ? new Date(toDate.getFullYear() - 1, 2, 1)
            : new Date(toDate.getFullYear() - 1, toDate.getMonth(), toDate.getDate() + 1);
    }

    private getPastThreeMonths(today: Date): Date {
        return this.isLeapDay(today)
            ? new Date(today.getFullYear() - 1, 11, 1)
            : new Date(today.getFullYear(), today.getMonth() - 3, today.getDate() + 1);
    }

    public componentWillReceiveProps(nextProps: IDateRangeProps) {
        if (nextProps.selectValue != this.props.selectValue) {
            this.setState({ dateSelected: nextProps.selectValue });
        }

        if (nextProps.minDate != this.props.minDate) {
            const _minDate = this.getMinDate(nextProps.minDate);
            this.setState({ fromMinDate: _minDate });
        }
        if (nextProps.fromDate != this.props.fromDate) {
            this.setState({ fromeDateSelected: nextProps.fromDate });
        }
        if (nextProps.toDate != this.props.toDate) {
            this.setState({ toDateSelected: nextProps.toDate });
        }
    }

    /***
       * Current date	        Last tax year	                    Current tax year
          31st May 2019	        6th April 2018 – 5th April 2019	    6th April 2019 –31st May 2019
          31st January 2019	    6th April 2017 – 5th April 2018	    6th April 2018 – 31st January 2019
       */
    private onDateSelectChange(key: string) {
        const { fromMinDate } = this.state;

        let fromDate: Date = null;
        let toDate: Date = null;
        let fromYear = null;
        let toYear = null;
        // const fromMonth = null;
        // const toMonth = null;
        const now = this.props.maxDate || new Date();
        const nowStr = JsonDateParser.getFormatDateString(now, "yyyy/MM/dd").split("/");
        const pointDate = new Date(Number(nowStr[0]), 3, 6);

        switch (key) {
            case DateOptions[1].key: //"Last tax year"
                if (now > pointDate) {
                    fromYear = Number(nowStr[0]) - 1;
                    toYear = Number(nowStr[0]);
                    fromDate = new Date(fromYear, 3, 6);
                    toDate = new Date(toYear, 3, 5);
                } else {
                    fromYear = Number(nowStr[0]) - 2;
                    toYear = Number(nowStr[0]) - 1;
                    fromDate = new Date(fromYear, 3, 6);
                    toDate = new Date(toYear, 3, 5);
                }
                break;
            case DateOptions[2].key: //"Current tax year"
                if (now > pointDate) {
                    fromYear = Number(nowStr[0]);
                    fromDate = new Date(fromYear, 3, 6);
                    toDate = now;
                } else {
                    fromYear = Number(nowStr[0]) - 1;
                    fromDate = new Date(fromYear, 3, 6);
                    toDate = now;
                }
                break;
            case DateOptions[0].key: //"Since account opening"
                fromDate = fromMinDate;
                toDate = now;
                break;
            case DateOptions[6].key: //"Custom"
                const isOlderThanOneYear = fromMinDate <= addMonths(now, -3);
                fromDate = isOlderThanOneYear ? this.getPastThreeMonths(now) : fromMinDate;
                toDate = now;
                break;
            case DateOptions[3].key: //"Past year"
                fromDate = this.getPastYear(now);
                toDate = now;
                break;
            case DateOptions[4].key: //"Past 6 months"
                fromDate = this.isLeapDay(now)
                    ? new Date(now.getFullYear() - 1, 8, 1)
                    : new Date(now.getFullYear(), now.getMonth() - 6, now.getDate() + 1);
                toDate = now;
                break;
            case DateOptions[5].key: //"Past 3 months"
                fromDate = this.getPastThreeMonths(now);
                toDate = now;
                break;
        }

        this.setState({ dateSelected: key, fromeDateSelected: fromDate, toDateSelected: toDate }, () => {
            this.props.onChange(fromDate, toDate, key == DateOptions[0].key, key);
        });
    }

    private renderLable(): JSX.Element {
        const { dateSelected, fromeDateSelected, toDateSelected, useShortMonthName } = this.state;

        if (dateSelected !== DateOptions[6].key) {
            const fromDateStr = JsonDateParser.getFormatDateString(
                fromeDateSelected,
                "dd MM yyyy",
                true,
                useShortMonthName
            );
            const toDateStr = JsonDateParser.getFormatDateString(toDateSelected, "dd MM yyyy", true, useShortMonthName);
            const cssClassNames = classNames(this.props.cssGridLabel);
            return (
                <div className={cssClassNames}>
                    <p className="text-size-14 mt-28">
                        From {fromDateStr} until {toDateStr}
                    </p>
                </div>
            );
        } else {
            return null;
        }
    }

    private renderFromDate(): JSX.Element {
        const { fromMinDate, fromeDateSelected, toDateSelected, dateSelected } = this.state;

        if (dateSelected !== DateOptions[6].key) {
            return null;
        }
        const _selected = fromeDateSelected
            ? fromMinDate >= fromeDateSelected
                ? fromMinDate
                : fromeDateSelected
            : null;
        const cssClassNames = classNames(this.props.cssGridFrom);
        return (
            <div className={cssClassNames}>
                <DatePicker
                    required={Contents.CanNotEmpty}
                    label={Contents.From}
                    labelSRSufix="dd/mm/yyyy"
                    selected={_selected}
                    atLeast={fromMinDate ? { value: fromMinDate, errorMessage: Contents.DateCanNotInPast } : null}
                    atMost={{ value: toDateSelected, errorMessage: Contents.DateCanNotInFuture }}
                    showYearDropdown={true}
                    alwaysValidateOnUpdate
                    onChange={(d) =>
                        d
                            ? this.setState({ fromeDateSelected: d }, () => {
                                  this.props.onChange(
                                      d,
                                      toDateSelected,
                                      dateSelected == DateOptions[0].key,
                                      dateSelected,
                                      true
                                  );
                              })
                            : null
                    }
                    todayButton={this.props.excludeToday ? null : "Today"}
                    isClearable={false}
                    dateFormat={"dd/MM/yyyy"}
                    acceptDateFormats={[
                        "dd/MM/yyyy",
                        "dd-MM-yyyy",
                        "ddMMyyyy",
                        "dd.MM.yyyy",
                        "dd_MM_yyyy",
                        "dd MM yyyy",
                    ]}
                    acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                />
            </div>
        );
    }

    private renderToDate(): JSX.Element {
        const { fromeDateSelected, toDateSelected, dateSelected } = this.state;
        const now = this.props.maxDate || new Date();
        const cssClassNames = classNames(this.props.cssGridTo);

        if (dateSelected !== DateOptions[6].key) {
            return null;
        }

        return (
            <div className={cssClassNames}>
                <DatePicker
                    required={Contents.CanNotEmpty}
                    label={Contents.To}
                    alwaysValidateOnUpdate
                    labelSRSufix="dd/mm/yyyy"
                    selected={toDateSelected}
                    atLeast={{ value: fromeDateSelected, errorMessage: Contents.DateCanNotInPast }}
                    atMost={{ value: now, errorMessage: Contents.DateCanNotInFuture }}
                    showYearDropdown={true}
                    onChange={(d) =>
                        d
                            ? this.setState({ toDateSelected: d }, () => {
                                  this.props.onChange(
                                      fromeDateSelected,
                                      d,
                                      dateSelected == DateOptions[0].key,
                                      dateSelected,
                                      true
                                  );
                              })
                            : null
                    }
                    todayButton={this.props.excludeToday ? null : "Today"}
                    isClearable={false}
                    dateFormat={"dd/MM/yyyy"}
                    acceptDateFormats={["dd/MM/yyyy", "dd-MM-yyyy", "ddMMyyyy"]}
                    acceptDateFormatsErrorMsg={Contents.AcceptDateFormatsErrorMsg}
                />
            </div>
        );
    }

    public render(): JSX.Element {
        const { dateSelected } = this.state;

        const DateRange = SelectEditorFactory.create<string>();
        let _options = DateOptions.map((item) => item.key);
        if (this.props.excludeSinceAccountOpening) {
            _options = _options.filter((ele) => ele != "1");
        }
        const cssClassNames = classNames(this.props.cssGridDate);
        return (
            <div className="row narrow-gutter">
                <div className={cssClassNames}>
                    <DateRange
                        disabled={this.props.disabled}
                        key="Date"
                        label={this.props.label || "Date"}
                        selected={dateSelected}
                        options={_options}
                        getText={(x) => CommonHelper.getOptionsTxt(x, DateOptions)}
                        onChange={(val) => this.onDateSelectChange(val)}
                    />
                </div>
                {this.renderLable()}
                {this.renderFromDate()}
                {this.renderToDate()}
            </div>
        );
    }
}
